import { useCallback } from 'react'
import { useCookies } from 'react-cookie'

const COOKIE_NAMES = {
  state: 'transcriptSurveyState',
  snoozeTime: 'transcriptSurveySnoozeTime',
  snoozeClicks: 'transcriptSurveySnoozeClicks',
  snoozeCount: 'transcriptSurveySnoozeCount',
} as const

const ALL_COOKIE_NAMES = Object.values(COOKIE_NAMES)

type SurveyState = 'pending' | 'snoozed' | 'completed'

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const COOKIE_OPTIONS = { path: '/', maxAge: COOKIE_MAX_AGE } as const
const REMOVE_OPTIONS = { path: '/' } as const
const SNOOZE_DURATION_MS = 24 * 60 * 60 * 1000
const SNOOZE_CLICK_THRESHOLD = 5
const MAX_SNOOZE_COUNT = 1

export const useTranscriptSurveyPrompt = () => {
  const [cookies, setCookie, removeCookie] = useCookies(ALL_COOKIE_NAMES)

  const clearSnoozeCookies = useCallback(() => {
    removeCookie(COOKIE_NAMES.snoozeTime, REMOVE_OPTIONS)
    removeCookie(COOKIE_NAMES.snoozeClicks, REMOVE_OPTIONS)
    removeCookie(COOKIE_NAMES.snoozeCount, REMOVE_OPTIONS)
  }, [removeCookie])

  const markCompleted = useCallback(() => {
    setCookie(COOKIE_NAMES.state, 'completed', COOKIE_OPTIONS)
    clearSnoozeCookies()
  }, [setCookie, clearSnoozeCookies])

  const checkShouldShowSurvey = useCallback((): boolean => {
    const state: SurveyState = cookies[COOKIE_NAMES.state] ?? 'pending'

    if (state === 'completed') {
      return false
    }

    if (state === 'snoozed') {
      const snoozeTime = Number(cookies[COOKIE_NAMES.snoozeTime] ?? 0)
      const snoozeClicks = Number(cookies[COOKIE_NAMES.snoozeClicks] ?? 0)
      const timePassed = Date.now() - snoozeTime >= SNOOZE_DURATION_MS
      const clicksMet = snoozeClicks >= SNOOZE_CLICK_THRESHOLD
      return timePassed && clicksMet
    }

    return true
  }, [cookies])

  const recordClick = useCallback(() => {
    const state: SurveyState = cookies[COOKIE_NAMES.state] ?? 'pending'
    if (state === 'snoozed') {
      const currentClicks = Number(cookies[COOKIE_NAMES.snoozeClicks] ?? 0)
      setCookie(COOKIE_NAMES.snoozeClicks, currentClicks + 1, COOKIE_OPTIONS)
    }
  }, [cookies, setCookie])

  const handleTakeSurvey = useCallback(() => {
    markCompleted()
  }, [markCompleted])

  const handleMaybeLater = useCallback(() => {
    const currentSnoozeCount = Number(cookies[COOKIE_NAMES.snoozeCount] ?? 0)
    const newSnoozeCount = currentSnoozeCount + 1

    if (newSnoozeCount > MAX_SNOOZE_COUNT) {
      markCompleted()
    } else {
      setCookie(COOKIE_NAMES.state, 'snoozed', COOKIE_OPTIONS)
      setCookie(COOKIE_NAMES.snoozeCount, newSnoozeCount, COOKIE_OPTIONS)
      setCookie(COOKIE_NAMES.snoozeTime, Date.now(), COOKIE_OPTIONS)
      setCookie(COOKIE_NAMES.snoozeClicks, 0, COOKIE_OPTIONS)
    }
  }, [cookies, setCookie, markCompleted])

  return {
    checkShouldShowSurvey,
    recordClick,
    handleTakeSurvey,
    handleMaybeLater,
  }
}
